// Department detail — opens over the stage without navigating away from Brain
// Home, per the contract's interaction model. On mobile the stylesheet turns
// it into a bottom sheet; the markup is the same.
//
// It reports counts and nothing else. There is no health score and no
// "performing well" language: the backend deliberately returns per-node status
// only, and inventing a judgment on top of it is the thing §3.5's doctrine
// forbids. Where a number is not measured, it says so rather than showing 0.
import React, { useEffect, useRef } from "react";

import { STATES } from "./brainState";

const NOTE = {
  [STATES.HEALTHY]:
    "Nothing is running in this department right now. That is a quiet state, not a fault.",
  [STATES.ACTIVE]: "Runs are in flight. Counts update as they finish.",
  [STATES.REASONING]:
    "A reasoning cycle is running: evidence is moving to the core, and a decision will come back.",
  [STATES.ATTENTION]:
    "Something here needs a decision from you. Open the notification to see what.",
  [STATES.CRITICAL]:
    "A system this department depends on is failing. Other departments are unaffected unless they show the same state.",
};

export default function DepartmentDetail({ department, onClose }) {
  const ref = useRef(null);

  // Move focus into the panel so keyboard users land where the content went.
  useEffect(() => {
    if (ref.current) ref.current.focus();
  }, [department.id]);

  return (
    <div
      className="cb-detail"
      role="dialog"
      aria-label={`${department.label} detail`}
      tabIndex={-1}
      ref={ref}
      data-testid="cb-detail"
      onClick={(e) => e.stopPropagation()}
    >
      <button type="button" className="cb-detail-close" onClick={onClose} aria-label="Close department detail">×</button>
      <h3>{department.label}</h3>
      <span className="cb-detail-state">{department.state}</span>

      <dl className="cb-detail-rows">
        <div><dt>Agents</dt><dd>{department.total}</dd></div>
        <div><dt>Live</dt><dd>{department.live}</dd></div>
        <div><dt>Dormant</dt><dd>{department.dormant}</dd></div>
        {department.suspended > 0 ? (
          <div><dt>Suspended</dt><dd>{department.suspended}</dd></div>
        ) : null}
        <div><dt>Current runs</dt><dd>{department.runs}</dd></div>
      </dl>

      <p className="cb-detail-note">{NOTE[department.state] || NOTE[STATES.HEALTHY]}</p>
    </div>
  );
}
